import React, { useEffect, useState, useMemo } from "react";
import Navbar from "../Navbar";
import { Link } from "react-router-dom";
import { Button, Grid } from "@mui/material";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ExposureIcon from "@mui/icons-material/Exposure";
import BootstrapTable from "react-bootstrap-table-next";
import "bootstrap/dist/css/bootstrap.min.css";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory, { textFilter } from "react-bootstrap-table2-filter";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { removeFilterTitle } from "../../util/removeFilterTitle";


// -------------------------------------
// ⏳ DEBOUNCE HOOK (Prevents dropdown lag)
// -------------------------------------
function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}



function DownloadCertificate() {

  // -------------------------------------
  // DROPDOWN VALUE + LOCALSTORAGE
  // -------------------------------------
  const [productInput, setProductInput] = useState(() => ({
    vertical_name: localStorage.getItem("vertical_name") || "",
  }));

  const handleEvent = (e) => {
    const value = e.target.value;
    setProductInput(prev => ({ ...prev, vertical_name: value }));
    localStorage.setItem("vertical_name", value);
  };

  // -------------------------------------
  // SUBLIST DATA
  // -------------------------------------
  const [sublist, setSublist] = useState([]);

  // -------------------------------------
  // DEBOUNCED DROPDOWN VALUE
  // -------------------------------------
  const debouncedVertical = useDebounce(productInput.vertical_name, 500);

  // useEffect(() => {
  //   removeFilterTitle(".sr-only");

  //   // Auto fetch data if vertical already selected
  //   if (productInput.vertical_name) {
  //     fetchVerticalData(productInput.vertical_name);
  //   }
  // }, []);

  // -------------------------------------
  // API CALL (only after debounce!)
  // -------------------------------------
  useEffect(() => {
    removeFilterTitle(".sr-only");
    if (!debouncedVertical) return;

    axios.get(`api/get_verticaldata/${debouncedVertical}`).then((res) => {
      if (res.data.status === 200) {
        const data =
          debouncedVertical === "Industrial-MA" ||
          debouncedVertical === "Industrial-MZ"
            ? res.data.IndCertificate
            : res.data.Certificate;

        setSublist(data || []);
      }
    });
  }, [debouncedVertical]);

  // -------------------------------------
  // MUI THEME
  // -------------------------------------
  const theme = createTheme({
    palette: {
      apple: { main: "#A38A00" },
    },
  });

  // -------------------------------------
  // MEMOIZED COLUMNS (Huge boost)
  // -------------------------------------
  const columns = useMemo(() => [
    { 
      dataField: "id", 
      text: "ID",
      headerStyle: { width: "5%", textAlign: "center" },
      style: { width: "5%", textAlign: "center" }
    },
    { 
      dataField: "memo_no",
      text: "Memo No", filter: textFilter(),
      headerStyle: { width: "10%", textAlign: "center" },
      style: { width: "10%", textAlign: "center" } 
    },
    { 
      dataField: "party_code",
      text: "Party Code", filter: textFilter(),
      headerStyle: { width: "8%", textAlign: "center" },
      style: { width: "8%", textAlign: "center" } 
    },
    { 
      dataField: "date",
      text: "Date",
      headerStyle: { width: "8%", textAlign: "center" },
      style: { width: "8%", textAlign: "center" },
      filter: textFilter()
    },
    { 
      dataField: "month_of_test",
      text: "Month of Test",
      headerStyle: { width: "8%", textAlign: "center" },
      style: { width: "8%", textAlign: "center" },
      filter: textFilter()
    },
    { 
      dataField: "remarks",
      text: "Remarks",
      filter: textFilter()
    },
    { 
      dataField: "vertical_type",
      text: "Vertical Type",
      headerStyle: { width: "8%", textAlign: "center" },
      style: { width: "8%", textAlign: "center" },
    },

    {
      dataField: "action",
      text: "Actions",
      headerStyle: { width: "15%", textAlign: "center" },
      style: { width: "15%", textAlign: "center" },
      formatter: (cell, row) => {
        const id = row.id;
        const vertical = row.vertical_type;
        const amount = row.is_amount_added;

        return (
          <ThemeProvider theme={theme}>
            <Grid container spacing={1}>
              {/* VIEW */}
              <Grid item xs={3}>
                <Button
                  component={Link}
                  to={
                    vertical === "IDLR"
                      ? `/certificates/view_certificate/${id}`
                      : `/certificates/view_industrial_certificate/${id}`
                  }
                  color="apple"
                  size="medium"
                  startIcon={<VisibilityIcon />}
                />
              </Grid>

              {/* EDIT */}
              <Grid item xs={3}>
                <Button
                  component={Link}
                  to={
                    vertical === "IDLR"
                      ? `/certificates/edit_certificate/${id}`
                      : `/certificates/update_industrial_certificate/${id}`
                  }
                  color="apple"
                  size="medium"
                  startIcon={<EditIcon />}
                />
              </Grid>

              <Grid item xs={3}>
                <Button
                  component={Link}
                  to={
                    vertical === "IDLR"
                      ? `/certificates/create_bill/${id}`
                      : `/certificates/create_ind_bill/${id}`
                  }
                  color="apple"
                  size="medium"
                  startIcon={<ReceiptIcon />}
                  disabled={amount === "Yes"}
                />
              </Grid>

              {/* BILL */}
              <Grid item xs={3}>
                <Button
                  component={Link}
                  to={
                    vertical === "IDLR"
                      ? `/certificates/update_bill/${id}`
                      : `/certificates/update_ind_bill/${id}`
                  }
                  color="apple"
                  size="medium"
                  startIcon={<ExposureIcon />}
                />
              </Grid>
            </Grid>
          </ThemeProvider>
        );
      },
    },

    { 
      dataField: "is_amount_added",
      text: "Amount Added",
      filter: textFilter(),
      headerStyle: { width: "8%", textAlign: "center" },
      style: { width: "8%", textAlign: "center" } 
    },
    { 
      dataField: "amount",
      text: "Amount",
      filter: textFilter(),
      headerStyle: { width: "8%", textAlign: "center" },
      style: { width: "8%", textAlign: "center" } 
    },
  ], []);

  return (
    <div>
      <Navbar />

      <div className="card px-8 m-5">

        <div className="card-header">
          <h4>Download Certificate</h4>

          <label className="required" style={{ marginBottom: "7px" }}>
            Vertical Type
          </label>

          <select
            id="vertical_name"
            name="vertical_name"
            className="form-control"
            value={productInput.vertical_name}
            onChange={handleEvent}
          >
            <option value="">-- Vertical Type --</option>
            <option value="Industrial-MZ">Industrial-MZ</option>
            <option value="Industrial-MA">Industrial-MA</option>
            <option value="IDLR">IDLR</option>
          </select>
        </div>

        <BootstrapTable
          keyField="id"
          data={sublist}
          columns={columns}
          hover
          condensed
          filter={filterFactory()}
          pagination={paginationFactory({ sizePerPage: 200 })}
          wrapperClasses="table-responsive"
          bootstrap4
          striped
          bordered={false}
          classes="table table-bordered"
        />
      </div>
    </div>
  );
}

export default DownloadCertificate;
